import React from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui';
import { ChartTrendingUpIcon, PencilIcon, PrinterIcon } from '../../components/ui/Icons';
import CrewFileTabs from './CrewFileTabs';
import { CrewIdentityHeader } from './CrewIdentityHeader';
import { crewFileToForm, emptyCrewForm, type CrewFormState } from './formMappers';
import { moduleConfig } from './config';
import { parseCrewRouteId } from './crewRouteId';
import { useCrewFile, useCrewLookups } from './useData';
import { useAuth } from '../../contexts/AuthContext';
import { CrewFilePrintSectionsModal } from './reports/CrewFilePrintSectionsModal';
import {
  CREW_FILE_PRINT_SECTIONS,
  countSelectedCrewFilePrintSections,
  crewFilePrintSectionsToParam,
  defaultCrewFilePrintSections,
  type CrewFilePrintSectionSelection,
} from './reports/crewFilePrintSections';

const FileView: React.FC = () => {
  const { colors } = useTheme();
  const { user } = useAuth();
  const agencyMode = String(user?.typeLocation ?? '').toUpperCase() === 'MANNING_AGENT';
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const crewId = parseCrewRouteId(id);
  const { data: item, isLoading } = useCrewFile(crewId);
  const { lookups, isLoading: loadingLookups } = useCrewLookups();
  const [form, setForm] = React.useState<CrewFormState>(emptyCrewForm);
  const [printOpen, setPrintOpen] = React.useState(false);
  const [printSections, setPrintSections] = React.useState<CrewFilePrintSectionSelection>(() =>
    defaultCrewFilePrintSections(true),
  );

  React.useEffect(() => {
    if (item) setForm(crewFileToForm(item));
  }, [item]);

  if (!crewId) {
    return <Navigate to="/crew/list" replace />;
  }

  if (isLoading || loadingLookups) {
    return (
      <div className={cn('flex items-center justify-center flex-1 min-h-[240px] text-sm', colors.textMuted)}>
        Loading...
      </div>
    );
  }

  if (!item) {
    return (
      <div className={cn('flex items-center justify-center flex-1 min-h-[240px] text-sm', colors.textMuted)}>
        Crew file not found.
      </div>
    );
  }

  const openPrintPreview = (next: CrewFilePrintSectionSelection) => {
    setPrintSections(next);
    setPrintOpen(false);
    const q = new URLSearchParams();
    if (countSelectedCrewFilePrintSections(next) < CREW_FILE_PRINT_SECTIONS.length) {
      q.set('sections', crewFilePrintSectionsToParam(next));
    }
    const qs = q.toString();
    navigate(`${moduleConfig.module.route.filePrint(item.id)}${qs ? `?${qs}` : ''}`);
  };

  return (
    <div className="h-full flex flex-col min-h-0 -mt-1">
      <div
        className={cn(
          'flex-1 flex flex-col min-h-0 overflow-hidden rounded-xl border shadow-sm',
          colors.surface,
          colors.border,
        )}
      >
        <CrewIdentityHeader
          item={item}
          embedded
          fallbackPath={
            agencyMode
              ? moduleConfig.module.route.list
              : crewId
                ? moduleConfig.module.route.detail(crewId)
                : moduleConfig.module.route.list
          }
          backTitle="Back"
          actions={
            <>
              <Button type="button" variant="outline" size="sm" onClick={() => setPrintOpen(true)}>
                <PrinterIcon className="h-4 w-4" />
                Print
              </Button>
              {!agencyMode && (
                <Link to={moduleConfig.module.route.detail(item.id)}>
                  <Button type="button" variant="outline" size="sm">
                    <ChartTrendingUpIcon className="h-4 w-4" />
                    Dashboard
                  </Button>
                </Link>
              )}
              <Link to={moduleConfig.module.route.edit(item.id)}>
                <Button type="button" variant="accent" size="sm">
                  <PencilIcon className="h-4 w-4" />
                  Edit
                </Button>
              </Link>
            </>
          }
        />

        <CrewFileTabs
          form={form}
          setForm={setForm}
          lookups={lookups}
          readOnly
          crewId={item.id}
          documents={item.documents}
          sectionAccess={lookups?.sectionAccess ?? (item as any)?.sectionAccess}
          agencyMode={agencyMode}
        />
      </div>

      <CrewFilePrintSectionsModal
        open={printOpen}
        value={printSections}
        onClose={() => setPrintOpen(false)}
        onConfirm={openPrintPreview}
        confirmLabel="Continue"
      />
    </div>
  );
};

export default FileView;
